import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthScreen } from '../components/AuthScreen'
import type { TFunction } from 'i18next'
import { PORTAL_ACCESS_DENIED_CODE, REFUSAL_COPY } from '../lib/api'

/** What a navigation here may carry (D8): the refusal's error code and the
 * backend's message. */
export interface AccessDeniedState {
  code?: string
  message?: string
}

/** The copy for a refusal (MS-997 D8; spec §1-ctrl-deny): no grant — at
 * sign-in or revoked mid-session (`auth.portal_access_denied`), or no code
 * at all — gets the localised "ask an Admin under Settings › Access" text.
 * The other refusals the exchange answers (`auth.email_unverified`,
 * `auth.email_missing`, `auth.federated_identity`, cognito.py) get their own
 * localised copy — the same English as the backend's message — so those never
 * read as a missing grant.
 * A code this screen does not know keeps the backend's own message. */
function deniedSubtitle(t: TFunction, state: AccessDeniedState | null): string {
  const grantCopy = t('accessDenied.message')
  if (!state?.code || state.code === PORTAL_ACCESS_DENIED_CODE) return grantCopy
  const key = REFUSAL_COPY[state.code]
  if (key) return t(key)
  // An empty backend message falls back to the grant copy too (so not `??`).
  if (!state.message) return grantCopy
  return state.message
}

export function AccessDenied() {
  const { t } = useTranslation()
  const location = useLocation()
  const state = location.state as AccessDeniedState | null

  return (
    // Same shared screen as Login.tsx's bare/signed-out states (see
    // AuthScreen) — matches the prototype's single `#login-screen` element,
    // whose `showAccessDenied()` only toggles the `.login-signed-out` badge
    // (here reading "Access denied", not "You have been signed out") and
    // overwrites the subtitle with the refusal — unlike the signed-out
    // state, which leaves the subtitle at its default.
    <AuthScreen badgeLabel={t('accessDenied.title')} subtitle={deniedSubtitle(t, state)} />
  )
}
